"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "buildFpeSchemas", {
    enumerable: true,
    get: function() {
        return buildFpeSchemas;
    }
});
const _zod = require("zod");
const _fpeformdescriptor = require("./fpe-form-descriptor");
// Every message below is a fixed string. A rejected value (PHI) must never be
// copied into a message, because messages reach responses and logs.
const MESSAGES = {
    unknownKey: 'Unknown field.',
    notObject: 'Must be an object.',
    text: 'Must be text.',
    number: 'Must be a number.',
    date: 'Must be a date in YYYY-MM-DD form.',
    option: 'Choose one of the listed options.',
    list: 'Must be a list of options.',
    duplicateOption: 'Choose each option only once.',
    boolean: 'Must be yes or no.',
    hidden: 'Leave this field empty while it is hidden.',
    required: 'This field is required.'
};
const strictObjectError = (issue)=>issue.code === 'unrecognized_keys' ? MESSAGES.unknownKey : MESSAGES.notObject;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const isCalendarDate = (text)=>{
    const match = DATE_PATTERN.exec(text);
    if (match === null) {
        return false;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};
const optionValues = (field)=>(field.options ?? []).map((option)=>option.value);
const valueSchema = (field)=>{
    switch(field.kind){
        case 'text':
        case 'textarea':
            {
                const maxLength = field.maxLength ?? _fpeformdescriptor.FPE_TEXT_MAX_LENGTH_DEFAULT;
                return _zod.z.string({
                    message: MESSAGES.text
                }).max(maxLength, {
                    message: `Must be at most ${maxLength} characters.`
                });
            }
        case 'number':
            {
                let schema = _zod.z.number({
                    message: MESSAGES.number
                });
                if (field.min !== undefined) {
                    schema = schema.min(field.min, {
                        message: `Must be at least ${field.min}.`
                    });
                }
                if (field.max !== undefined) {
                    schema = schema.max(field.max, {
                        message: `Must be at most ${field.max}.`
                    });
                }
                return schema;
            }
        case 'date':
            {
                return _zod.z.string({
                    message: MESSAGES.date
                }).refine(isCalendarDate, {
                    message: MESSAGES.date
                });
            }
        case 'select':
            {
                return _zod.z.enum(optionValues(field), {
                    message: MESSAGES.option
                });
            }
        case 'multiselect':
            {
                const values = optionValues(field);
                return _zod.z.array(_zod.z.enum(values, {
                    message: MESSAGES.option
                }), {
                    message: MESSAGES.list
                }).max(values.length, {
                    message: MESSAGES.duplicateOption
                }).refine((selected)=>new Set(selected).size === selected.length, {
                    message: MESSAGES.duplicateOption
                });
            }
        case 'yesno':
        case 'checkbox':
            {
                return _zod.z.boolean({
                    message: MESSAGES.boolean
                });
            }
        default:
            {
                throw new Error(`FPE field ${field.key} has no answer schema for kind ${field.kind}`);
            }
    }
};
const isMissing = (field, value)=>{
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (field.kind === 'checkbox') {
        return value !== true;
    }
    return false;
};
const answerFields = (section)=>section.fields.filter((field)=>!(0, _fpeformdescriptor.isCustomFpeFieldKind)(field.kind));
const sectionSchema = (fields)=>{
    const shape = {};
    for (const field of fields){
        shape[field.key] = valueSchema(field).nullable().optional();
    }
    return _zod.z.strictObject(shape, {
        error: strictObjectError
    }).superRefine((sectionAnswers, ctx)=>{
        for (const field of fields){
            const value = sectionAnswers[field.key];
            const hasValue = value !== null && value !== undefined;
            if (hasValue && !(0, _fpeformdescriptor.isFpeFieldVisible)(field, sectionAnswers)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [
                        field.key
                    ],
                    message: MESSAGES.hidden
                });
            }
        }
    });
};
const buildFpeSchemas = (descriptor)=>{
    const problems = (0, _fpeformdescriptor.validateFpeFormDescriptor)(descriptor);
    if (problems.length > 0) {
        throw new Error(`Invalid FPE form descriptor ${descriptor.formVersion}: ${problems.join('; ')}`);
    }
    const answerSections = descriptor.sections.map((section)=>({
            section,
            fields: answerFields(section)
        })).filter(({ fields })=>fields.length > 0);
    const shape = {};
    for (const { section, fields } of answerSections){
        shape[section.key] = sectionSchema(fields).optional();
    }
    const draftSchema = _zod.z.strictObject(shape, {
        error: strictObjectError
    });
    const completeSchema = draftSchema.superRefine((answers, ctx)=>{
        for (const { section, fields } of answerSections){
            const sectionAnswers = answers[section.key] ?? {};
            for (const field of fields){
                if (!field.required || !(0, _fpeformdescriptor.isFpeFieldVisible)(field, sectionAnswers)) {
                    continue;
                }
                if (isMissing(field, sectionAnswers[field.key])) {
                    ctx.addIssue({
                        code: 'custom',
                        path: [
                            section.key,
                            field.key
                        ],
                        message: MESSAGES.required
                    });
                }
            }
        }
    });
    return {
        draftSchema: draftSchema,
        completeSchema: completeSchema
    };
};
